import { eventsSchema } from './event.schema';
import type { EventSlug, TourEvent } from './event.types';

/* =============================================================================
 * CONFIG DE LOS 5 DESTINOS · la UNICA fuente de datos comerciales del sitio.
 * -----------------------------------------------------------------------------
 * PROCEDENCIA de cada dato (nada aqui esta inventado):
 *   fechas, horarios, tz, titulos ...... ADR-003 §1 (manda sobre cualquier doc viejo)
 *   bloques de programa ................ docs/brief/02_BRIEFING_ESTRATEGIA.md §1
 *   ganchos de venta ................... docs/brief/02_BRIEFING_ESTRATEGIA.md §2 (literal)
 *   taglines y beneficios .............. copy leido en los 6 mockups aprobados
 *   venue / precio / aforo / entradas .. NO EXISTEN -> null + `coming-soon` (ADR-003 §2-3)
 *   temas de la FAQ .................... master §7 + ADR-006 (ver bloque de abajo)
 *
 * REGLA: ninguna pagina escribe datos comerciales a mano. Si falta un campo, se
 * pide a FORGE-ALPHA por `inbox/FORGE-ALPHA/`; no se inventa en `src/pages/`.
 *
 * -----------------------------------------------------------------------------
 * LA FAQ, Y POR QUE CADA RESPUESTA DICE LO QUE DICE
 * El master §7 fija OCHO temas: duracion · horarios · idioma · que llevar ·
 * accesibilidad · entradas · politica de cambios · contacto. Aqui estan los
 * ocho en las cinco sedes, y ni uno solo se rellena con un dato plausible:
 *
 *   SALE DEL DATO (`isPending: false`)
 *     duracion  -> aritmetica del propio `program`: sabado 2h+2h+3h = 7h y
 *                  domingo 3h = 10h. Cancun, su agenda por horas.
 *     horarios  -> ADR-003 §1, los mismos que valida el esquema.
 *     idioma    -> espanol en todas las sedes (orden del ORACLE
 *                  20260725; el brief no contempla otro idioma ni `hreflang`).
 *
 *   NO EXISTE (`isPending: true` -> la pagina rotula "POR ANUNCIAR")
 *     sede, precio, aforo, fecha de apertura de venta, politica de cambios,
 *     accesibilidad (depende de sede) y direccion de contacto directa.
 *     Se responde que se anunciara. PROHIBIDO redactar un dato verosimil.
 *
 *   QUE LLEVAR es el unico caso mixto: se responde SOLO con lo que se deduce
 *   del programa ya publicado (Cancun tiene cenote, playa al amanecer y cena de
 *   gala; las conferencias son sala y no piden material), y se marca pendiente
 *   porque la lista definitiva depende de una sede que no esta cerrada.
 *
 *   CONTACTO nombra unicamente los canales que el cliente YA tiene publicados
 *   (docs/brief/03_IDENTIDAD_VISUAL.md §"Redes y contacto"): AzraheldelMayor.com
 *   e Instagram @azraheldelmayor. No se inventa un correo de soporte.
 *
 * La FAQ del HUB no vive aqui: el hub vende la gira entera, no una sede, asi
 * que es contenido de pagina (`TourHubPage/hub.content.ts`, FORGE-CHARLIE).
 * ========================================================================== */

/** Horario comun de las 4 conferencias (ADR-003 §1). Domingo 10:30-13:30. */
const SATURDAY = {
  b1: ['10:00', '12:00'],
  /** El hueco entre el primer bloque y el segundo TIENE nombre desde el 5-ago-2026:
   *  el cliente lo publica como parada con servicio, no como aire muerto. */
  brunch: ['12:00', '12:30'],
  b2: ['12:30', '14:30'],
  b3: ['16:30', '19:00'],
} as const;
const SUNDAY_SINGLE = ['10:30', '13:30'] as const;

/**
 * El taller del domingo es el MISMO en las cuatro conferencias (encargo del
 * cliente, 30-jul-2026): antes cada ciudad le habia puesto su propio nombre y
 * eso hacia parecer que se vendian cuatro talleres distintos. Vive en una sola
 * constante para que no vuelva a divergir sede por sede.
 *
 * Cancun no lo lleva: su convivencia es de jueves a sabado y no tiene domingo.
 */
const TALLER_DOMINGO =
  'Taller: «El Umbral de Orión: Iniciación a la Autosanación. Arquitectura Cuántica. Dinámicas con lo Invisible»';

const rawEvents = [
  /* ---------------------------------------------------------------------------
   * FUNES · encargo del cliente por WhatsApp, 23-sep-2026.
   *
   * ES EL PRIMERO DE LA GIRA, no el ultimo de la lista: 2-3 de febrero (era
   * 19-20 de enero hasta el 26-oct-2026), tres dias antes de Buenos Aires. El orden de este array ES el orden cronologico
   * (lo exige el esquema), asi que entrar por delante no es una preferencia de
   * maquetacion, es el dato.
   *
   * NO VENDE ENTRADA. El cliente lo dijo con todas las letras («no hace falta
   * entradas»): se reserva escribiendo al organizador por WhatsApp, con el
   * mensaje que el mismo redacto. De ahi el estado `whatsapp`.
   *
   * TODO LO DE AQUI SALE DE SU FLYER, y lo que el flyer no dice no esta:
   *   fecha, sede, direccion y telefono ... rotulados en el propio flyer
   *   promesa y los cuatro pilares ....... rotulados en el propio flyer
   *   HORARIOS ........................... los mando aparte el 26-oct-2026
   * Un dia inventado para que el esquema quedara "completo" habria sido
   * exactamente lo que ADR-003 prohibe.
   * ------------------------------------------------------------------------ */
  {
    slug: 'funes',
    path: '/funes',
    order: 1,
    city: 'Funes',
    shortName: 'Funes',
    country: 'Argentina',
    countryCode: 'AR',
    flag: '\u{1F1E6}\u{1F1F7}',
    startDate: '2027-02-02',
    endDate: '2027-02-03',
    /* Santa Fe va en la zona de Cordoba, no en la de Buenos Aires. Mismo
     * desfase horario hoy, distinta zona: se declara la suya. */
    timeZone: 'America/Argentina/Cordoba',
    title: 'Convivencia «Encuentro con el Ser»',
    titleMain: 'Convivencia',
    titleAccent: '«Encuentro con el Ser»',
    scriptClaim: 'Un lugar para recordar quién eres',
    tagline: 'Despierta tu verdad, transforma tu realidad y eleva tu conciencia.',
    promiseLines: ['Despierta tu verdad.', 'Transforma tu realidad.', 'Eleva tu conciencia.'],
    salesHook:
      'Dos días de convivencia en Alma Sana, entre los árboles de Funes: naturaleza y consciencia, sanación integral, comunidad y evolución. No es una conferencia con público: es un encuentro para recordar quién eres. Las plazas se reservan escribiendo al equipo por WhatsApp.',
    heroTone: 'ancestral',
    isVip: false,
    format: 'retiro',
    benefits: [
      { id: 'fu-naturaleza', icon: 'seed', title: 'Naturaleza y consciencia', description: null },
      { id: 'fu-sanacion', icon: 'lotus', title: 'Sanación integral', description: null },
      { id: 'fu-comunidad', icon: 'heart', title: 'Comunidad y evolución', description: null },
      { id: 'fu-paradigma', icon: 'sun', title: 'Un nuevo paradigma', description: null },
    ],
    /* Programa que mando el cliente el 26-oct-2026 («se me olvido decirte que
     * pusieras el programa de Funes, Santa Fe, que es este»), tal cual: titulos
     * con sus mayusculas, sin los emojis, y la fase a la que pertenece cada
     * bloque en su descripcion. El cierre del miércoles es «14:00 | Fin», sin hora
     * de fin. */
    program: [
      {
        day: 1,
        date: '2027-02-02',
        title: 'Trascendencia, Sanación Colectiva y Empoderamiento',
        shortTitle: null,
        blocks: [
          {
            id: 'fu-d1-recepcion',
            start: '09:00',
            end: '10:00',
            title: 'Recepción, Acreditaciones y Desayuno de Bienvenida',
            description: 'Fase I · Apertura y anclaje en la realidad presente',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-charla',
            start: '10:00',
            end: '11:15',
            title: 'Inauguración Oficial y Charla Magistral: Espiritualidad & Claves de Actualidad',
            description: 'Fase I · Apertura y anclaje en la realidad presente',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-preguntas',
            start: '11:15',
            end: '12:00',
            title: 'Ronda Abierta de Preguntas y Respuestas',
            description: 'Fase I · Apertura y anclaje en la realidad presente',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-pausa',
            start: '12:00',
            end: '12:30',
            title: 'Pausa / Coffee Break',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-cuerda',
            start: '12:30',
            end: '13:30',
            title: 'Dinámica Vivencial: «La Cuerda Roja» (El Tejido de la Unidad)',
            description: 'Fase II · Interconexión, unidad y liberación',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-perdon',
            start: '13:30',
            end: '14:30',
            title: 'Taller Práctico: «La Rueda del Perdón»',
            description: 'Fase II · Interconexión, unidad y liberación',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-almuerzo',
            start: '14:30',
            end: '17:00',
            title: 'Almuerzo Compartido y Tiempo de Descanso / Integración',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-caminata',
            start: '17:00',
            end: '17:50',
            title: 'Dinámica de Autoconfianza: «Caminata a Ciegas con Antifaz»',
            description: 'Fase III · Confianza ciega, empoderamiento y Cámara Cuántica',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-pasillo',
            start: '17:50',
            end: '18:20',
            title: 'Dinámica: «El Pasillo de Subida de Autoestima y Empoderamiento»',
            description: 'Fase III · Confianza ciega, empoderamiento y Cámara Cuántica',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d1-camara',
            start: '18:20',
            end: '19:00',
            title: 'Cierre del Día: Sesión de «Cámara Cuántica de Biorregulación Energética»',
            description: 'Fase III · Confianza ciega, empoderamiento y Cámara Cuántica',
            isHighlight: true,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
      {
        day: 2,
        date: '2027-02-03',
        title: 'Tecnología Onírica, Cirugía Pleyadiana y Salto Evolutivo',
        shortTitle: null,
        blocks: [
          {
            id: 'fu-d2-recepcion',
            start: '09:00',
            end: '10:00',
            title: 'Recepción',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d2-onirico',
            start: '10:00',
            end: '11:30',
            title: 'Protocolo de iniciación hacia la sanación, encuentro con las energías oníricas',
            description: 'Fase IV · Consciencia del sueño y cirugía energética',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d2-teoria',
            start: '11:30',
            end: '12:15',
            title: 'Mini-Explicación Teórica: ¿Qué es la Cirugía Pleyadiana?',
            description: 'Fase IV · Consciencia del sueño y cirugía energética',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d2-cirugia',
            start: '12:15',
            end: '13:15',
            title: 'Sesión Práctica y Demostración en Vivo de Cirugía Pleyadiana',
            description: 'Fase IV · Consciencia del sueño y cirugía energética',
            isHighlight: true,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'fu-d2-cierre',
            start: '14:00',
            end: null,
            title: 'Fin y cierre del evento',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
    ],
    /* Los OCHO temas del master §7, como las otras cuatro sedes, mas sede y
     * precio. Lo que el flyer no dice se responde «te lo confirma el equipo»
     * y va marcado `isPending`: ni un dato verosimil. */
    faq: [
      {
        id: 'fu-faq-cuando',
        question: '¿Qué días y a qué hora es?',
        answer:
          'Martes 2 y miércoles 3 de febrero de 2027, en Funes (Santa Fe). El martes de 9:00 a 19:00 y el miércoles de 9:00 a 14:00, hora de Argentina.',
        isPending: false,
      },
      {
        id: 'fu-faq-duracion',
        question: '¿Cuánto dura en total?',
        answer: 'Dos jornadas de convivencia: el martes de 9:00 a 19:00, con desayuno y almuerzo compartidos, y el miércoles de 9:00 a 14:00.',
        isPending: false,
      },
      {
        id: 'fu-faq-sede',
        question: '¿Dónde se celebra?',
        answer: 'En Alma Sana, medicina integrativa: Guido y ruta 9, S2132 Funes, Santa Fe (almasanafunes.com).',
        isPending: false,
      },
      {
        id: 'fu-faq-idioma',
        question: '¿En qué idioma es?',
        answer: 'Íntegramente en español, impartido por Azrahel del Mayor.',
        isPending: false,
      },
      {
        id: 'fu-faq-llevar',
        question: '¿Qué necesito llevar?',
        answer: 'La lista de lo que conviene traer se confirmará al reservar tu lugar.',
        isPending: true,
      },
      {
        id: 'fu-faq-accesibilidad',
        question: '¿El espacio es accesible?',
        answer:
          'Las condiciones de acceso del espacio se confirmarán al reservar. Si necesitas algo concreto, dilo al escribir y te responden antes de confirmar.',
        isPending: true,
      },
      {
        id: 'fu-faq-entradas',
        question: '¿Cómo reservo mi lugar?',
        answer:
          'Esta convivencia no vende entrada por internet: se reserva escribiendo al +54 341 681 6281, el equipo de comunicación de Azrahel del Mayor.',
        isPending: false,
      },
      {
        id: 'fu-faq-precio',
        question: '¿Cuánto cuesta?',
        answer: 'El importe y la forma de pago se confirmarán al escribir para reservar.',
        isPending: true,
      },
      {
        id: 'fu-faq-cambios',
        question: '¿Puedo cambiar o cancelar mi reserva?',
        answer: 'Las condiciones de cambio y cancelación se confirmarán al reservar.',
        isPending: true,
      },
      {
        id: 'fu-faq-contacto',
        question: '¿Con quién hablo si tengo dudas?',
        answer:
          'Con el equipo de comunicación de Azrahel del Mayor, en el +54 341 681 6281. También en AzraheldelMayor.com y en Instagram @azraheldelmayor.',
        isPending: false,
      },
    ],
    venue: 'Alma Sana · Guido y ruta 9, S2132 Funes, Santa Fe',
    /* El «enlace de compra» de este destino es el chat. Lleva el mensaje que
     * escribio el cliente, ya redactado, para que quien pulsa no tenga que
     * explicar a que evento se apunta. */
    ticketUrl:
      'https://wa.me/543416816281?text=Estoy%20interesado%20en%20asistir%20al%20evento%20CONVIVENCIA%20de%20Azrahel%2C%20en%20Funes%2C%20Santa%20Fe%2C%20Argentina.',
    salesState: 'whatsapp',
    ticketTiers: [],
    priceFrom: null,
    capacity: null,
  },
  {
    slug: 'buenos-aires',
    path: '/buenos-aires',
    order: 2,
    city: 'Buenos Aires',
    shortName: 'Buenos Aires',
    country: 'Argentina',
    countryCode: 'AR',
    flag: '🇦🇷',
    startDate: '2027-02-06',
    endDate: '2027-02-07',
    timeZone: 'America/Argentina/Buenos_Aires',
    title: 'Desprogramación de la Percepción y Salto Cuántico',
    titleMain: 'Desprogramación de la Percepción',
    titleAccent: 'y Salto Cuántico',
    scriptClaim: null,
    tagline: 'Rompe el motor del ego y las repeticiones de la Matrix. Recupera tu automaestría.',
    promiseLines: ['Rompe el motor del ego y las repeticiones de la Matrix.', 'Recupera tu automaestría.'],
    salesHook:
      'Ya te diste cuenta de que las fórmulas espirituales genéricas no funcionan. Estás atrapado en un bucle diseñado por la Matrix: los 1.000 sueños del yo perdido. En Buenos Aires no venimos a teorizar; el sábado desmantelamos la ingeniería cuántica del ego y el domingo encendemos tu propia tecnología interna. Si buscas respuestas con sustancia y reclamar tu auto-maestría, este mapa es para vos.',
    heroTone: 'celeste',
    isVip: false,
    format: 'conferencia',
    benefits: [
      { id: 'ba-desprograma', icon: 'eye', title: 'Desprograma creencias limitantes', description: null },
      { id: 'ba-bucles', icon: 'infinity', title: 'Libérate de bucles de control', description: null },
      { id: 'ba-creador', icon: 'star', title: 'Activa tu poder creador', description: null },
      { id: 'ba-verdad', icon: 'lotus', title: 'Vive desde tu verdad esencial', description: null },
    ],
    program: [
      {
        day: 1,
        date: '2027-02-06',
        title: 'Desprogramación de la Percepción y Salto Cuántico',
        shortTitle: null,
        blocks: [
          {
            id: 'ba-d1-b1',
            start: SATURDAY.b1[0],
            end: SATURDAY.b1[1],
            title: 'Atrapados en los 1.000 Sueños del Yo Perdido',
            description: 'Desmantelar la ingeniería del ego y los bucles de la Matrix.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'ba-d1-brunch',
            start: SATURDAY.brunch[0],
            end: SATURDAY.brunch[1],
            title: 'Brunch',
            description: 'Cafés, tés y pastas.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'ba-d1-b2',
            start: SATURDAY.b2[0],
            end: SATURDAY.b2[1],
            title: 'Año 2040, El Fin de los Tiempos',
            description: 'Análisis geopolítico-holográfico, historia galáctica y mapa de la transición.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'ba-d1-b3',
            start: SATURDAY.b3[0],
            end: SATURDAY.b3[1],
            title: 'Experiencia de Calibración Cuántica',
            description: 'Sesión grupal en vivo con la Cámara Cuántica Pleyadiana.',
            isHighlight: true,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
      {
        day: 2,
        date: '2027-02-07',
        title: 'Activación de la Auto-Maestría y Soberanía',
        shortTitle: null,
        blocks: [
          {
            id: 'ba-d2-b1',
            start: SUNDAY_SINGLE[0],
            end: SUNDAY_SINGLE[1],
            title: TALLER_DOMINGO,
            description:
              'Conexión directa con la Fuente, activación de telepatía interna, lectura del Campo Akáshico, clarividencia, clariaudiencia y precognición.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
    ],
    faq: [
      {
        id: 'ba-faq-cuando',
        question: '¿Qué días y a qué hora es?',
        answer:
          'Sábado 6 de febrero de 2027 en cuatro tramos (10:00-12:00, brunch 12:00-12:30, 12:30-14:30 y 16:30-19:00) y domingo 7 de febrero en un bloque único de 10:30 a 13:30, hora de Buenos Aires.',
        isPending: false,
      },
      {
        id: 'ba-faq-duracion',
        question: '¿Cuánto dura en total?',
        answer:
          'Diez horas de programa repartidas en dos días: siete el sábado, en tres bloques con su brunch y sus pausas, y tres el domingo en un bloque único.',
        isPending: false,
      },
      {
        id: 'ba-faq-sede',
        question: '¿Dónde se celebra?',
        answer: 'La sede se anunciará junto con la apertura de entradas.',
        isPending: true,
      },
      {
        id: 'ba-faq-idioma',
        question: '¿En qué idioma es?',
        answer: 'Íntegramente en español, impartido por Azrahel del Mayor.',
        isPending: false,
      },
      {
        id: 'ba-faq-llevar',
        question: '¿Qué necesito llevar?',
        answer:
          'Ropa cómoda y ganas de trabajar contigo: el programa publicado no pide traer ningún material. La lista completa de recomendaciones se publicará junto con la sede.',
        isPending: true,
      },
      {
        id: 'ba-faq-accesibilidad',
        question: '¿El espacio es accesible?',
        answer:
          'Las condiciones de accesibilidad dependen de la sede, que todavía no está confirmada. Se publicarán aquí en cuanto se anuncie, con el detalle de accesos y plazas adaptadas.',
        isPending: true,
      },
      {
        id: 'ba-faq-entradas',
        question: '¿Cuánto cuesta y cómo compro mi entrada?',
        answer:
          'Ya están a la venta. Se compran en la tienda oficial, en el botón de entradas de esta misma página: te lleva a la ficha de tu ciudad, con los tipos de entrada y sus precios.',
        isPending: false,
      },
      {
        id: 'ba-faq-cambios',
        question: '¿Puedo cambiar o devolver mi entrada?',
        answer:
          'La política de cambios y devoluciones se publicará junto con las condiciones de venta, antes de que se pueda comprar ninguna entrada.',
        isPending: true,
      },
      {
        id: 'ba-faq-contacto',
        question: '¿Con quién hablo si tengo dudas?',
        answer:
          'Con el Equipo de comunicación 📣 de la Escuela Pleyadiana AΩGR, por WhatsApp al +34 722 29 39 67. También en la web AzraheldelMayor.com y en la cuenta @azraheldelmayor.',
        isPending: false,
      },
    ],
    venue: null,
    ticketUrl: 'https://entradas.eventoazrahel.com/azrahel/buenos-aires/',
    salesState: 'on-sale',
    ticketTiers: [],
    priceFrom: null,
    capacity: null,
  },
  {
    slug: 'bogota',
    path: '/bogota',
    order: 3,
    city: 'Bogotá',
    shortName: 'Bogotá',
    country: 'Colombia',
    countryCode: 'CO',
    flag: '🇨🇴',
    startDate: '2027-02-13',
    endDate: '2027-02-14',
    timeZone: 'America/Bogota',
    title: 'Sanación de la Consciencia y Frecuencia Origen',
    titleMain: 'Sanación de la Consciencia y Frecuencia Origen',
    titleAccent: null,
    scriptClaim: null,
    tagline: 'El llamado de tu linaje estelar para volver a casa.',
    promiseLines: ['El llamado de tu linaje estelar', 'para volver a casa.'],
    salesHook:
      'Tu alma sabe que este plano tridimensional no es tu hogar original; sientes el llamado latente de tu linaje estelar. Tras calibrar tu campo en la Cámara Cuántica el sábado, el domingo despertaremos tus sentidos sutiles y saldrás con la antena de tu glándula pineal encendida. Es hora de volver a casa.',
    heroTone: 'calido',
    isVip: false,
    format: 'conferencia',
    benefits: [
      {
        id: 'bo-sanacion',
        icon: 'heart',
        title: 'Sanación profunda',
        description: 'de la consciencia',
      },
      {
        id: 'bo-alineacion',
        icon: 'mountain',
        title: 'Alineación con tu',
        description: 'Frecuencia Origen',
      },
      { id: 'bo-linaje', icon: 'seed', title: 'Activación de tu', description: 'linaje estelar' },
      { id: 'bo-recordar', icon: 'star', title: 'Recordar quién eres', description: 'para volver a Casa' },
    ],
    program: [
      {
        day: 1,
        date: '2027-02-13',
        title: 'Sanación de la Consciencia y Frecuencia Origen',
        shortTitle: null,
        blocks: [
          {
            id: 'bo-d1-b1',
            start: SATURDAY.b1[0],
            end: SATURDAY.b1[1],
            title: 'Liberación del Alma — Los 1.000 Sueños del Yo Perdido',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'bo-d1-brunch',
            start: SATURDAY.brunch[0],
            end: SATURDAY.brunch[1],
            title: 'Brunch',
            description: 'Cafés, tés y pastas.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'bo-d1-b2',
            start: SATURDAY.b2[0],
            end: SATURDAY.b2[1],
            title: 'Año 2040, Tiempo del Fin',
            description: 'Mapa hacia la Nueva Humanidad Galáctica.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'bo-d1-b3',
            start: SATURDAY.b3[0],
            end: SATURDAY.b3[1],
            title: 'Encuentro Estelar en la Cámara Cuántica',
            description: 'Armonización colectiva.',
            isHighlight: true,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
      {
        day: 2,
        date: '2027-02-14',
        title: 'Recordar el Poder del Espíritu',
        shortTitle: null,
        blocks: [
          {
            id: 'bo-d2-b1',
            start: SUNDAY_SINGLE[0],
            end: SUNDAY_SINGLE[1],
            title: TALLER_DOMINGO,
            description:
              'Escuchar la Voz de la Fuente, despertar la telepatía interna, precognición y apertura de dones para leer campos cuánticos akáshicos.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
    ],
    faq: [
      {
        id: 'bo-faq-cuando',
        question: '¿Qué días y a qué hora es?',
        answer:
          'Sábado 13 de febrero de 2027 en cuatro tramos (10:00-12:00, brunch 12:00-12:30, 12:30-14:30 y 16:30-19:00) y domingo 14 de febrero en un bloque único de 10:30 a 13:30, hora de Bogotá.',
        isPending: false,
      },
      {
        id: 'bo-faq-duracion',
        question: '¿Cuánto dura en total?',
        answer:
          'Diez horas de programa repartidas en dos días: siete el sábado, en tres bloques con su brunch y sus pausas, y tres el domingo en un bloque único.',
        isPending: false,
      },
      {
        id: 'bo-faq-sede',
        question: '¿Dónde se celebra?',
        answer: 'La sede se anunciará junto con la apertura de entradas.',
        isPending: true,
      },
      {
        id: 'bo-faq-idioma',
        question: '¿En qué idioma es?',
        answer: 'Íntegramente en español, impartido por Azrahel del Mayor.',
        isPending: false,
      },
      {
        id: 'bo-faq-llevar',
        question: '¿Qué necesito llevar?',
        answer:
          'Ropa cómoda y ganas de trabajar contigo: el programa publicado no pide traer ningún material. La lista completa de recomendaciones se publicará junto con la sede.',
        isPending: true,
      },
      {
        id: 'bo-faq-accesibilidad',
        question: '¿El espacio es accesible?',
        answer:
          'Las condiciones de accesibilidad dependen de la sede, que todavía no está confirmada. Se publicarán aquí en cuanto se anuncie, con el detalle de accesos y plazas adaptadas.',
        isPending: true,
      },
      {
        id: 'bo-faq-entradas',
        question: '¿Cuánto cuesta y cómo compro mi entrada?',
        answer:
          'Ya están a la venta. Se compran en la tienda oficial, en el botón de entradas de esta misma página: te lleva a la ficha de tu ciudad, con los tipos de entrada y sus precios.',
        isPending: false,
      },
      {
        id: 'bo-faq-cambios',
        question: '¿Puedo cambiar o devolver mi entrada?',
        answer:
          'La política de cambios y devoluciones se publicará junto con las condiciones de venta, antes de que se pueda comprar ninguna entrada.',
        isPending: true,
      },
      {
        id: 'bo-faq-contacto',
        question: '¿Con quién hablo si tengo dudas?',
        answer:
          'Con el Equipo de comunicación 📣 de la Escuela Pleyadiana AΩGR, por WhatsApp al +34 722 29 39 67. También en la web AzraheldelMayor.com y en la cuenta @azraheldelmayor.',
        isPending: false,
      },
    ],
    venue: null,
    ticketUrl: 'https://entradas.eventoazrahel.com/azrahel/bogota/',
    salesState: 'on-sale',
    ticketTiers: [],
    priceFrom: null,
    capacity: null,
  },
  {
    slug: 'mexico',
    path: '/mexico',
    order: 4,
    city: 'Ciudad de México',
    shortName: 'México',
    country: 'México',
    countryCode: 'MX',
    flag: '🇲🇽',
    startDate: '2027-02-20',
    endDate: '2027-02-21',
    timeZone: 'America/Mexico_City',
    title: 'Linaje Ancestral, Épica y Anclaje Mayor',
    titleMain: 'Linaje Ancestral, Épica y Anclaje Mayor',
    titleAccent: null,
    scriptClaim: null,
    tagline: 'Dos días para recordar quiénes somos, de dónde venimos y hacia dónde vamos juntos.',
    promiseLines: ['Dos días para recordar quiénes somos,', 'de dónde venimos y hacia dónde vamos juntos.'],
    salesHook:
      'México lleva la memoria del contacto estelar en la sangre, pero la Matrix intentó dormir tu poder. El reloj corre hacia el 2040. El sábado revelaremos el mapa del fin de los tiempos, y el domingo romperemos el velo de la tercera dimensión. Te espero en el Anclaje Mayor.',
    heroTone: 'ancestral',
    isVip: false,
    format: 'conferencia',
    benefits: [
      {
        id: 'mx-memoria',
        icon: 'spiral',
        title: 'Memoria ancestral',
        description: 'Recordamos la sabiduría de nuestros linajes para sanar, honrar y avanzar.',
      },
      {
        id: 'mx-destino',
        icon: 'star',
        title: 'Destino colectivo',
        description: 'Alineamos nuestra fuerza para servir al bien mayor y al futuro que soñamos.',
      },
      {
        id: 'mx-anclaje',
        icon: 'sun',
        title: 'El Anclaje Mayor',
        description: 'Activamos el puente entre el cielo, la tierra y la humanidad.',
      },
    ],
    program: [
      {
        day: 1,
        date: '2027-02-20',
        title: 'El Despertar del Linaje y el Retorno Estelar',
        shortTitle: 'Linaje y Memoria',
        blocks: [
          {
            id: 'mx-d1-b1',
            start: SATURDAY.b1[0],
            end: SATURDAY.b1[1],
            title: 'Ruptura del Pacto con el Olvido — Los 1.000 Sueños del Yo',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'mx-d1-brunch',
            start: SATURDAY.brunch[0],
            end: SATURDAY.brunch[1],
            title: 'Brunch',
            description: 'Cafés, tés y pastas.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'mx-d1-b2',
            start: SATURDAY.b2[0],
            end: SATURDAY.b2[1],
            title: 'Año 2040, El Fin de los Tiempos',
            description: 'Profecías galácticas y destino de México.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'mx-d1-b3',
            start: SATURDAY.b3[0],
            end: SATURDAY.b3[1],
            title: 'El Gran Anclaje de la Red Estelar',
            description: 'Activación masiva con la Cámara Cuántica.',
            isHighlight: true,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
      {
        day: 2,
        date: '2027-02-21',
        title: 'Despertar de Dones y Conexión Divina',
        shortTitle: 'Épica y Anclaje Mayor',
        blocks: [
          {
            id: 'mx-d2-b1',
            start: SUNDAY_SINGLE[0],
            end: SUNDAY_SINGLE[1],
            title: TALLER_DOMINGO,
            description:
              'Aprender a escuchar la Voz de la Fuente, activación de clarividencia, clariaudiencia, precognición, telepatía con familias estelares y lectura de Registros Akáshicos.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
    ],
    faq: [
      {
        id: 'mx-faq-cuando',
        question: '¿Qué días y a qué hora es?',
        answer:
          'Sábado 20 de febrero de 2027 en cuatro tramos (10:00-12:00, brunch 12:00-12:30, 12:30-14:30 y 16:30-19:00) y domingo 21 de febrero en un bloque único de 10:30 a 13:30, hora de Ciudad de México.',
        isPending: false,
      },
      {
        id: 'mx-faq-duracion',
        question: '¿Cuánto dura en total?',
        answer:
          'Diez horas de programa repartidas en dos días: siete el sábado, en tres bloques con su brunch y sus pausas, y tres el domingo en un bloque único.',
        isPending: false,
      },
      {
        id: 'mx-faq-sede',
        question: '¿Dónde se celebra?',
        answer: 'La sede se anunciará junto con la apertura de entradas.',
        isPending: true,
      },
      {
        id: 'mx-faq-idioma',
        question: '¿En qué idioma es?',
        answer: 'Íntegramente en español, impartido por Azrahel del Mayor.',
        isPending: false,
      },
      {
        id: 'mx-faq-llevar',
        question: '¿Qué necesito llevar?',
        answer:
          'Ropa cómoda y ganas de trabajar contigo: el programa publicado no pide traer ningún material. La lista completa de recomendaciones se publicará junto con la sede.',
        isPending: true,
      },
      {
        id: 'mx-faq-accesibilidad',
        question: '¿El espacio es accesible?',
        answer:
          'Las condiciones de accesibilidad dependen de la sede, que todavía no está confirmada. Se publicarán aquí en cuanto se anuncie, con el detalle de accesos y plazas adaptadas.',
        isPending: true,
      },
      {
        id: 'mx-faq-entradas',
        question: '¿Cuánto cuesta y cómo compro mi entrada?',
        answer:
          'Ya están a la venta. Se compran en la tienda oficial, en el botón de entradas de esta misma página: te lleva a la ficha de tu ciudad, con los tipos de entrada y sus precios.',
        isPending: false,
      },
      {
        id: 'mx-faq-cambios',
        question: '¿Puedo cambiar o devolver mi entrada?',
        answer:
          'La política de cambios y devoluciones se publicará junto con las condiciones de venta, antes de que se pueda comprar ninguna entrada.',
        isPending: true,
      },
      {
        id: 'mx-faq-contacto',
        question: '¿Con quién hablo si tengo dudas?',
        answer:
          'Con el Equipo de comunicación 📣 de la Escuela Pleyadiana AΩGR, por WhatsApp al +34 722 29 39 67. También en la web AzraheldelMayor.com y en la cuenta @azraheldelmayor.',
        isPending: false,
      },
    ],
    venue: null,
    ticketUrl: 'https://entradas.eventoazrahel.com/azrahel/mexico/',
    salesState: 'on-sale',
    ticketTiers: [],
    priceFrom: null,
    capacity: null,
  },
  {
    slug: 'cancun',
    path: '/cancun',
    order: 5,
    city: 'Cancún / Riviera Maya',
    shortName: 'Cancún',
    country: 'México',
    countryCode: 'MX',
    flag: '🇲🇽',
    startDate: '2027-02-25',
    endDate: '2027-02-27',
    timeZone: 'America/Cancun',
    title: 'LEVEL UP: El Despertar del Guerrero — Conciencia y Transformación',
    titleMain: 'LEVEL UP: El Despertar del Guerrero — Conciencia y Transformación',
    titleAccent: null,
    scriptClaim: 'Despierta tu poder. Vive tu propósito.',
    tagline: 'Retiro vivencial premium · Comité íntimo',
    promiseLines: ['Retiro vivencial premium', 'Comité íntimo'],
    salesHook:
      'Sabes que la teoría ya no es suficiente. La Convivencia en Cancún no es un retiro convencional; es LEVEL UP: El Despertar del Guerrero — Conciencia y Transformación. En petit comité, vas a vivir un empujón directo y de alto rendimiento: meditación al amanecer, cenote, Cámara Cuántica, fogata en la playa y tertulias de coaching mano a mano conmigo. Si estás listo para el salto definitivo a tu Auto-Maestría, tu lugar está aquí.',
    heroTone: 'caribe',
    isVip: true,
    format: 'retiro',
    // El mockup de Cancun no usa fila de beneficios: usa programa con miniaturas.
    benefits: [],
    program: [
      {
        day: 1,
        date: '2027-02-25',
        title: 'Cena de Presentación de los Participantes',
        shortTitle: null,
        blocks: [
          {
            id: 'cu-d1-cena-presentacion',
            start: '20:00',
            end: null,
            title: 'Cena de presentación de los participantes',
            description: null,
            isHighlight: true,
            highlightTitle: 'Bienvenida',
            highlightSubtitle: 'La primera noche',
          },
        ],
      },
      {
        day: 2,
        date: '2027-02-26',
        title: 'Purificación y Anclaje en la Playa',
        shortTitle: null,
        blocks: [
          {
            id: 'cu-d2-amanecer',
            start: '07:00',
            end: '08:00',
            title: 'Activación al Amanecer',
            description: 'Ejercicios de respiración y meditación frente al mar.',
            isHighlight: true,
            highlightTitle: 'Amanecer Sagrado',
            highlightSubtitle: 'Activación al alba',
          },
          {
            id: 'cu-d2-desayuno',
            start: '09:00',
            end: '10:00',
            title: 'Desayuno buffet',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d2-bio',
            start: '11:00',
            end: '12:00',
            title: 'Actividad Bioenergética',
            description: 'Calibración y desbloqueo de centros energéticos.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d2-cenote',
            start: '12:15',
            end: '14:30',
            title: 'Inmersión Consciente en el Cenote',
            description:
              'Visita al cenote sagrado. Limpieza de memorias energéticas en agua subterránea, preparación del campo y fotos de grupo en el cenote.',
            isHighlight: true,
            highlightTitle: 'Cenote & Purificación',
            highlightSubtitle: 'Inmersión y renacimiento',
          },
          {
            id: 'cu-d2-descanso',
            start: '15:00',
            end: '18:30',
            title: 'Comida y descanso',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d2-camara',
            start: '18:30',
            end: '20:30',
            title: 'Cámara Cuántica Pleyadiana al Atardecer',
            description: 'Sesión inmersiva grupal en vivo para elevar la frecuencia.',
            isHighlight: true,
            highlightTitle: 'Cámara Cuántica al Atardecer',
            highlightSubtitle: 'Expansión y realineación',
          },
          {
            id: 'cu-d2-cena',
            start: '20:30',
            end: '22:00',
            title: 'Cena grupal',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d2-tertulia',
            start: '22:00',
            end: null,
            title: 'Tertulia de Coaching y Punch Espiritual',
            description: 'Sesión de impacto directo, empoderamiento y motivación con Azrahel.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
      {
        day: 3,
        date: '2027-02-27',
        title: 'Activación de Poder y Fuego Estelar',
        shortTitle: null,
        blocks: [
          {
            id: 'cu-d3-respiracion',
            start: '07:00',
            end: '08:00',
            title: 'Respiración y Meditación de Reconexión',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d3-desayuno',
            start: '09:00',
            end: '10:00',
            title: 'Desayuno',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d3-bio',
            start: '11:00',
            end: '12:00',
            title: 'Actividad Bioenergética',
            description: 'Canalización y sensibilidad sutil.',
            isHighlight: true,
            highlightTitle: 'Activación del Guerrero',
            highlightSubtitle: 'Cuerpo, mente y espíritu',
          },
          {
            id: 'cu-d3-espejo',
            start: '12:15',
            end: '14:30',
            title: 'Taller «Lectura del Espejo Cuántico»',
            description:
              'Dinámica de grupo a la sombra, en salón o palapa ventilada: los asistentes trabajan en parejas su telepatía y la percepción del campo del compañero.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d3-siesta',
            start: '15:00',
            end: '18:30',
            title: 'Comida y siesta',
            description: null,
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
          {
            id: 'cu-d3-fogata',
            start: '18:30',
            end: '20:30',
            title: 'Fogata Ritual en la Playa',
            description:
              'Conexión con el elemento fuego, quema de antiguos patrones y promesas de soberanía al mar.',
            isHighlight: true,
            highlightTitle: 'Ritual del Fuego en la Playa',
            highlightSubtitle: 'Integración y propósito',
          },
          {
            id: 'cu-d3-gala',
            start: '20:30',
            end: '22:00',
            title: 'Cena de gala de despedida',
            description: null,
            isHighlight: true,
            highlightTitle: 'Cena de Gala',
            highlightSubtitle: 'Celebración & Hermandad',
          },
          {
            id: 'cu-d3-cierre',
            start: '22:00',
            end: null,
            title: 'Tertulia de Cierre de Alto Empuje',
            description: 'Círculo de compromiso, reactivación de dones y fuerza para el regreso a casa.',
            isHighlight: false,
            highlightTitle: null,
            highlightSubtitle: null,
          },
        ],
      },
    ],
    faq: [
      {
        id: 'cu-faq-cuando',
        question: '¿Qué días es la convivencia?',
        answer:
          'Del 25 al 27 de febrero de 2027, en formato de retiro. El 25 se llega y se cena todos juntos; los dos días completos son el 26 y el 27, de amanecer a tertulia de noche.',
        isPending: false,
      },
      {
        // Cancun NO es una conferencia: su FAQ se responde con SU agenda, no
        // con la plantilla de las cuatro ciudades (ADR-006, aviso de ECHO).
        id: 'cu-faq-duracion',
        question: '¿Cómo son las jornadas y cuánto duran?',
        answer:
          'Tres días, y solo el primero es corto: el 25 la convivencia empieza a las 20:00 con la cena de presentación. El 26 y el 27 son jornadas completas —no dos mañanas de sala—: arrancan a las 07:00 con la activación al amanecer y la tertulia de cierre empieza a las 22:00, sin hora final marcada.',
        isPending: false,
      },
      {
        id: 'cu-faq-donde',
        question: '¿Dónde es?',
        answer:
          'Cancún / Riviera Maya · Palmaïa, The House of AïA. Los detalles logísticos exactos se confirmarán junto con la apertura de plazas.',
        isPending: true,
      },
      {
        id: 'cu-faq-idioma',
        question: '¿En qué idioma es?',
        answer: 'Íntegramente en español, impartido por Azrahel del Mayor.',
        isPending: false,
      },
      {
        id: 'cu-faq-llevar',
        question: '¿Qué hay que llevar?',
        answer:
          'La propia agenda lo adelanta: hay inmersión en cenote, amanecer y fogata en la playa y una cena de gala de despedida. Con eso, ropa de baño, ropa cómoda para las actividades y algo más arreglado para la última noche. La lista definitiva de equipaje se anunciará junto con los detalles logísticos.',
        isPending: true,
      },
      {
        id: 'cu-faq-accesibilidad',
        question: '¿Las actividades son accesibles?',
        answer:
          'La convivencia combina sala, playa y cenote, y los detalles logísticos de la sede aún no están confirmados. Las condiciones de accesibilidad de cada actividad se publicarán aquí en cuanto se cierren.',
        isPending: true,
      },
      {
        id: 'cu-faq-plazas',
        question: '¿Cuántas plazas hay?',
        /* Texto literal del cliente (docx «Modificaciones en la web», 14-sep-2026). */
        answer: 'Solo para 12 asistentes.',
        isPending: false,
      },
      {
        id: 'cu-faq-entradas',
        question: '¿Cómo reservo mi plaza?',
        answer:
          'Ya está abierta. Se reserva en la tienda oficial, desde el botón de esta misma página, que te lleva a la ficha de la convivencia con las plazas y sus precios.',
        isPending: false,
      },
      {
        id: 'cu-faq-cambios',
        question: '¿Puedo cambiar o ceder mi plaza?',
        answer:
          'La política de cambios, cesión y devolución se publicará junto con las condiciones de reserva, antes de que se pueda reservar ninguna plaza.',
        isPending: true,
      },
      {
        id: 'cu-faq-contacto',
        question: '¿Con quién hablo si tengo dudas?',
        answer:
          'Con el Equipo de comunicación 📣 de la Escuela Pleyadiana AΩGR, por WhatsApp al +34 722 29 39 67. También en la web AzraheldelMayor.com y en la cuenta @azraheldelmayor.',
        isPending: false,
      },
    ],
    // Formulacion segura obligatoria (ADR-003 §3): sin direccion postal ni mapa.
    venue: 'Cancún / Riviera Maya · Palmaïa, The House of AïA',
    ticketUrl: 'https://entradas.eventoazrahel.com/azrahel/cancun/',
    salesState: 'on-sale',
    ticketTiers: [],
    priceFrom: null,
    capacity: null,
  },
] as const;

/**
 * Validacion en el ARRANQUE. Si un dato viola el ADR-003, la app no monta.
 * Fallo ruidoso > web mintiendo sobre entradas.
 */
export const EVENTS: readonly TourEvent[] = eventsSchema.parse(rawEvents);

export const EVENTS_BY_SLUG: Readonly<Record<EventSlug, TourEvent>> = Object.freeze(
  Object.fromEntries(EVENTS.map((event) => [event.slug, event])) as Record<EventSlug, TourEvent>,
);

export const EVENT_SLUGS: readonly EventSlug[] = EVENTS.map((event) => event.slug);

export function getEventBySlug(slug: string): TourEvent | undefined {
  return (EVENTS_BY_SLUG as Record<string, TourEvent | undefined>)[slug];
}
